import { isBefore, fromUnixTime } from 'date-fns'
import { jwtDecode } from 'jwt-decode'
import { use, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { QueryRefsContext } from '@/components/root/query-refs-context'
import { SamlSessionContext } from '@/components/saml-session/context'
import { TOKEN_KEY } from '@/config/local-storage'
import { useReadQuery } from '@/lib/hooks'

type DecodedSamlToken = {
  samlExp?: number
  idSamlCompany?: string | null
}

const decodeSamlFromToken = (): DecodedSamlToken => {
  try {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
      return {}
    }
    const decoded = jwtDecode<DecodedSamlToken>(token)
    return {
      samlExp: decoded.samlExp,
      idSamlCompany: decoded.idSamlCompany,
    }
  } catch {
    return {}
  }
}

export const SamlSessionProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { samlInterstitialQueryRef } = use(QueryRefsContext)
  const { data } = useReadQuery(samlInterstitialQueryRef)
  const location = useLocation()

  const [tokenState, setTokenState] = useState(decodeSamlFromToken)

  // Re-evaluate SAML token state on navigation
  useEffect(() => {
    setTokenState(decodeSamlFromToken())
  }, [location.pathname])

  // Re-evaluate when TokenUpdater refreshes the token
  useEffect(() => {
    const handler = () => setTokenState(decodeSamlFromToken())
    window.addEventListener('token-updated', handler)
    return () => window.removeEventListener('token-updated', handler)
  }, [])

  const samlRestrictedCompanies = useMemo(
    () => data?.user?.saml?.samlInterstitialData ?? [],
    [data]
  )

  const hasActiveSamlSession = useCallback(
    (idCompany: string) => {
      if (tokenState.samlExp == null || tokenState.idSamlCompany == null) {
        return false
      }
      return (
        tokenState.idSamlCompany === idCompany &&
        isBefore(new Date(), fromUnixTime(tokenState.samlExp))
      )
    },
    [tokenState.samlExp, tokenState.idSamlCompany]
  )

  const value = useMemo(
    () => ({ samlRestrictedCompanies, hasActiveSamlSession }),
    [samlRestrictedCompanies, hasActiveSamlSession]
  )

  return (
    <SamlSessionContext.Provider value={value}>
      {children}
    </SamlSessionContext.Provider>
  )
}
